"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SideNavigation = exports.SideNavigationItemType = void 0;
const react_1 = __importDefault(require("react"));
const react_router_1 = require("react-router");
const core_1 = require("@material-ui/core");
const Divider_1 = __importDefault(require("@material-ui/core/Divider"));
const List_1 = __importDefault(require("@material-ui/core/List"));
const ChevronRight_1 = __importDefault(require("@material-ui/icons/ChevronRight"));
const ExpandMore_1 = __importDefault(require("@material-ui/icons/ExpandMore"));
const ListItem_1 = __importDefault(require("@material-ui/core/ListItem"));
const uuid_1 = require("uuid");
const Box_1 = __importDefault(require("../../layouts/Box"));
const Inline_1 = __importDefault(require("../../layouts/Inline"));
const Link_1 = __importDefault(require("../Link"));
const SideNavigationTemplate_1 = __importDefault(require("./components/SideNavigationTemplate"));
const useStyles = core_1.makeStyles((theme) => ({
    drawerList: {
        padding: 0,
    },
    drawerItems: {
        paddingTop: '10px',
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    divider: {
        marginTop: '25px',
        marginBottom: '25px',
    },
    section: {
        marginTop: theme.spacing(1.4),
        marginBottom: theme.spacing(1.4),
    },
    sectionText: {
        fontWeight: 'bold',
    },
    sectionHeader: {
        marginLeft: -theme.spacing(2),
    },
    nestedItem: {
        paddingLeft: theme.spacing(1),
    },
    launchIcon: {
        verticalAlign: 'middle',
        width: '16px',
    },
    listItem: {
        marginTop: '10px',
        marginBottom: '10px',
        padding: 0,
        '& [class*="-colorSecondary"]': {
            fontWeight: 'bold',
        },
        '& .MuiLink-root': {
            color: theme.palette.grey[600],
        },
        '& .MuiLink-root:hover': {
            color: theme.palette.secondary.main,
        },
    },
}));
var SideNavigationItemType;
(function (SideNavigationItemType) {
    SideNavigationItemType["DIVIDER"] = "divider";
    SideNavigationItemType["LINK"] = "link";
    SideNavigationItemType["SECTION"] = "section";
})(SideNavigationItemType = exports.SideNavigationItemType || (exports.SideNavigationItemType = {}));
/** Populate the initial expanded state of the section based on the settings */
const populateInitialExpandedState = (items, initialState = {}) => {
    items.forEach((item) => {
        if (item.expanded && item.text !== undefined) {
            initialState[item.text] = true;
        }
        if (item.items && item.items.length > 0) {
            populateInitialExpandedState(item.items, initialState);
        }
    });
    return initialState;
};
/**
 * The side navigation refers to a list of links that point to the pages within an application.
 */
const SideNavigation = ({ header, items = [], location }) => {
    const classes = useStyles({});
    const [expandedSections, setExpandedSections] = react_1.default.useState(populateInitialExpandedState(items));
    const expandToggle = (itemText) => {
        setExpandedSections((prevExpandedSections) => (Object.assign(Object.assign({}, prevExpandedSections), { [itemText]: !prevExpandedSections[itemText] })));
    };
    const wrapInHref = (children, href) => {
        return href ? (react_1.default.createElement("div", { style: { width: '100%' } },
            react_1.default.createElement(Link_1.default, { href: href, underlineHover: false }, children))) : (children);
    };
    const renderDivider = () => react_1.default.createElement(Divider_1.default, { key: uuid_1.v4(), className: classes.divider });
    const renderItemLabel = (item, cssClass) => {
        const itemColor = location.pathname === item.href ? 'secondary' : 'inherit';
        return wrapInHref(react_1.default.createElement(Inline_1.default, { spacing: "xs" },
            react_1.default.createElement(core_1.Typography, { color: itemColor, className: cssClass }, item.text),
            item.info), item.href);
    };
    const renderListItem = (item, cssClass) => {
        const itemText = item.text || '';
        const itemLabel = renderItemLabel(item, cssClass);
        if (item.text !== undefined && item.items && item.items.length > 0) {
            return (react_1.default.createElement("div", { key: uuid_1.v4() },
                react_1.default.createElement(ListItem_1.default, { button: true, onClick: () => expandToggle(itemText), className: classes.sectionHeader },
                    expandedSections[itemText] ? react_1.default.createElement(ExpandMore_1.default, null) : react_1.default.createElement(ChevronRight_1.default, null),
                    react_1.default.createElement("b", null, itemLabel)),
                react_1.default.createElement(core_1.Collapse, { key: `${item.text}-collapse`, in: expandedSections[itemText], timeout: "auto", unmountOnExit: true },
                    react_1.default.createElement(List_1.default, { component: "div", disablePadding: true, className: classes.nestedItem }, item.items.map((i) => renderNavigationItem(i))))));
        }
        else {
            return (react_1.default.createElement(ListItem_1.default, { key: uuid_1.v4(), className: classes.listItem }, itemLabel));
        }
    };
    const renderSection = (item) => (react_1.default.createElement("div", { className: classes.section, key: uuid_1.v4() }, renderListItem(item, classes.sectionText)));
    const renderNavigationItem = (item) => {
        switch (item.type) {
            case SideNavigationItemType.DIVIDER:
                return renderDivider();
            case SideNavigationItemType.LINK:
                return renderListItem(item);
            case SideNavigationItemType.SECTION:
                return renderSection(item);
            default:
                return null;
        }
    };
    return (react_1.default.createElement(SideNavigationTemplate_1.default, { header: header },
        react_1.default.createElement(Box_1.default, { className: classes.drawerItems },
            react_1.default.createElement(List_1.default, { className: classes.drawerList }, items.map((item) => renderNavigationItem(item))))));
};
exports.SideNavigation = SideNavigation;
/**
 * The side navigation refers to a list of links that point to the pages within a service.
 */
exports.default = react_router_1.withRouter(exports.SideNavigation);
//# sourceMappingURL=index.js.map